'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import FormattedMessage from 'I18n/components/FormattedMessage';
import { getGAClassName } from 'pricing-pages-lib/utils/publicPageUrls';
import { useSelector } from 'react-redux';
import ProductFeaturesMap from 'self-service-api/constants/ProductFeaturesMap';
import { FREE } from 'self-service-api/constants/ProductLevels';
import { MARKETING, OPERATIONS, SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import { getProductFeatureDescriptionKey } from 'self-service-api/core/utilities/getFeatureDescriptionKey';
import H3 from 'UIComponents/elements/headings/H3';
import { MON607_PARAMETER_OPTIONS } from '../../constants/ExperimentConstants';
import FreeCardFeatureMap from '../../constants/FreeCardFeatureMap';
import { HighlightedProductsHeaderConfig } from '../../constants/HighlightedProductsHeaderConfig';
import ProductLineToGaMap from '../../constants/ProductLineToGaMap';
import { MERCH_CARD } from '../../constants/Tracking';
import { isAppAuthSelector } from '../../selectors/ConfigSelectors';
import { isMon569VariantSelector, mon607VariantSelector } from '../../selectors/ExperimentTreatmentSelectors';
import { hasPricingAndPurchasingSucceededSelector } from '../../selectors/LoadingStateSelectors';
import { highestProductLevelFromProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import ChecklistItem from '../common/ChecklistItem';
import CurrentPlanTag from '../common/CurrentPlanTag';
import { borderStyle, FillerRow, Section } from '../common/MerchCardSharedComponents';
import HighlightedFreePriceDisplay from '../common/pricing/HighlightedFreePriceDisplay';
import SkeletonLoadWrapper from '../loading/SkeletonLoadWrapper';
import SignupButton from '../public-only/SignupButton';
import BusinessObjectivesSSAPI from './business-objectives/BusinessObjectivesSSAPI';
import { BizLowerTierContainer } from './business-objectives/BusinessObjectivesStyledComponents';
import ViewAllFeatures from './ViewAllFeatures';

const HighlightedFreeCard = ({
  productLine,
  commonStyles
}) => {
  const descriptionLangMap = {
    [MARKETING]: 'pricingPage.freeProductCards.description.marketing',
    [SALES]: 'pricingPage.freeProductCards.description.sales',
    [SERVICE]: 'pricingPage.freeProductCards.description.service',
    [OPERATIONS]: 'pricingPage.freeProductCards.description.operations'
  };
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const isCurrentPlan = useSelector(reduxState => hasPricingAndPurchasingSucceeded && highestProductLevelFromProductInventorySelector(reduxState, {
    productLine
  }) === FREE && isAppAuthSelector(reduxState));
  const freeFeatures = productLine === OPERATIONS ? Object.keys(ProductFeaturesMap[productLine][FREE]) : FreeCardFeatureMap[productLine];
  const isMon569Variant = useSelector(isMon569VariantSelector);
  const mon607Variant = useSelector(mon607VariantSelector); // In order for the grid to work, we need this to remain enclosed in a Fragment and not a div.
  // Children of the display grid item must get the grid-start attributes directly

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Section, {
      className: "text-center p-top-6",
      style: Object.assign({
        position: 'relative',
        msGridRow: 1,
        gridRowStart: 1
      }, commonStyles),
      isMon569Variant: isMon569Variant,
      children: [isCurrentPlan && /*#__PURE__*/_jsx(CurrentPlanTag, {}), /*#__PURE__*/_jsx(H3, {
        className: "m-all-0",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: HighlightedProductsHeaderConfig[productLine][FREE].title
        })
      })]
    }), isMon569Variant ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Section, {
        className: "p-top-4 text-center",
        style: Object.assign({
          msGridRow: 2,
          gridRowStart: 2
        }, commonStyles),
        isMon569Variant: isMon569Variant,
        children: /*#__PURE__*/_jsx(HighlightedFreePriceDisplay, {})
      }), /*#__PURE__*/_jsx(Section, {
        className: "p-top-4 text-center",
        style: Object.assign({
          msGridRow: 3,
          gridRowStart: 3
        }, commonStyles),
        isMon569Variant: isMon569Variant,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: descriptionLangMap[productLine]
        })
      }), /*#__PURE__*/_jsx(FillerRow, {
        row: 4,
        commonStyles: commonStyles
      })]
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Section, {
        className: "p-top-4 text-center",
        style: Object.assign({
          msGridRow: 2,
          gridRowStart: 2
        }, commonStyles),
        isMon569Variant: isMon569Variant,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: descriptionLangMap[productLine]
        })
      }), /*#__PURE__*/_jsx(FillerRow, {
        row: 3,
        commonStyles: commonStyles
      }), /*#__PURE__*/_jsx(FillerRow, {
        row: 4,
        commonStyles: commonStyles
      })]
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-top-4",
      style: Object.assign({
        msGridRow: 5,
        gridRowStart: 5
      }, commonStyles),
      isMon569Variant: isMon569Variant,
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [47],
        children: /*#__PURE__*/_jsx(SignupButton, {
          sourceId: "pricing-hero",
          productLine: productLine,
          use: "primary",
          style: {
            width: '100%',
            lineHeight: '23px'
          }
        })
      })
    }), mon607Variant === MON607_PARAMETER_OPTIONS.CONTROL || productLine !== OPERATIONS ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Section, {
        isMon569Variant: isMon569Variant,
        className: "p-top-8",
        style: Object.assign({
          msGridRow: 6,
          gridRowStart: 6
        }, commonStyles),
        children: /*#__PURE__*/_jsx(BizLowerTierContainer, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "pricingPage.freeProductCards.bizObjectivesTitle"
          })
        })
      }), /*#__PURE__*/_jsx(Section, {
        isMon569Variant: isMon569Variant,
        style: Object.assign({
          msGridRow: 7,
          gridRowStart: 7
        }, commonStyles),
        children: freeFeatures.map(feature => /*#__PURE__*/_jsx("div", {
          className: "m-y-1 flex",
          children: /*#__PURE__*/_jsx(ChecklistItem, {
            tooltipMessage: getProductFeatureDescriptionKey(feature, productLine),
            message: `pricing-pages.features.${feature}.name`
          })
        }, feature))
      })]
    }) : /*#__PURE__*/_jsx(BusinessObjectivesSSAPI, {
      productLine: productLine,
      productLevel: FREE,
      commonStyles: commonStyles
    }), /*#__PURE__*/_jsx(Section, {
      className: "p-bottom-3 p-x-0",
      isMon569Variant: isMon569Variant,
      style: Object.assign({}, commonStyles, {
        msGridRow: 8,
        gridRowStart: 8
      }),
      showBorderBottom: false,
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !hasPricingAndPurchasingSucceeded,
        heights: [50],
        style: {
          width: '100%'
        },
        children: /*#__PURE__*/_jsx(ViewAllFeatures, {
          productLine: productLine,
          productLevel: FREE,
          gaClassName: getGAClassName(`${ProductLineToGaMap[productLine]}features`),
          trackingSource: MERCH_CARD
        })
      })
    }), /*#__PURE__*/_jsx(FillerRow, {
      row: 9,
      commonStyles: Object.assign({}, commonStyles, {
        borderTop: borderStyle
      }),
      isMon569Variant: isMon569Variant
    })]
  });
};

export default HighlightedFreeCard;